import { getCSRFHeaders, postJSON } from '@folklore/fetch';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { isAfter, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'wouter';

import { useThemeClassName } from '../../contexts/ThemeContext';
import StyledButton from '../buttons/StyledButton';
import HourGlass from '../icons/Hourglass';
import LiveGameCard from '../partials/LiveGameCard';
import Timer from '../partials/Timer';

import styles from '../../../styles/steps/wait.module.scss';

const propTypes = {
    game: PropTypes.shape({
        id: PropTypes.string,
    }),
    step: PropTypes.shape({
        id: PropTypes.string,
    }),
    participation: PropTypes.shape({
        id: PropTypes.string,
    }),
    isPreview: PropTypes.bool,
    isDisplay: PropTypes.bool,
    className: PropTypes.string,
};

const defaultProps = {
    game: null,
    step: null,
    participation: null,
    isPreview: false,
    isDisplay: false,
    className: null,
};

function Wait({ game, participation, isPreview, isDisplay, className }) {
    const route = useUrlGenerator();
    const [, navigate] = useLocation();
    const themeClassName = useThemeClassName(styles);
    const { startAt = null, subtitle = null, startTime = null, instructions = null } = game || {};

    const start = parseISO(startAt);
    const pastStart = isAfter(new Date(), start);
    const [ended, setEnded] = useState(pastStart);
    const onEnded = useCallback(() => setEnded(true), [setEnded]);

    const cancelParticipation = useCallback(() => {
        const { id = null } = participation || {};
        if (id !== null) {
            postJSON(route('participation.end', { participation: id }), null, {
                credentials: 'include',
                headers: getCSRFHeaders(),
            })
                .then(() => navigate(route('play')))
                .catch((e) => {
                    console.error(e);
                    navigate(route('play'));
                });
        }
    }, [participation]);

    const time = startTime || subtitle || null;

    return (
        <div
            className={classNames([
                styles.container,
                themeClassName,
                { [styles.isDisplay]: isDisplay, [className]: className !== null },
            ])}
        >
            <div className={styles.inner}>
                <HourGlass className={styles.icon} />
                <h4 className={styles.title}>
                    {!ended ? (
                        <FormattedMessage
                            defaultMessage="Your challenge starts in"
                            description="Step message"
                        />
                    ) : null}
                    {ended && time !== null ? (
                        <FormattedMessage
                            defaultMessage="Your challenge will start at {time}."
                            description="Step message"
                            values={{ time }}
                        />
                    ) : null}
                </h4>
                {!ended ? (
                    <Timer className={styles.timer} started date={start} onEnded={onEnded} />
                ) : null}
                {game !== null && !isPreview ? (
                    <>
                        <p className={styles.description}>
                            <FormattedMessage
                                defaultMessage="You have chosen"
                                description="Step message"
                            />
                        </p>
                        <LiveGameCard className={styles.liveGameCard} game={game} />
                        {instructions !== null ? (
                            <p className={styles.description}>
                                {instructions || (
                                    <FormattedMessage
                                        defaultMessage="Get to the Radio-Canada Space before the challenge."
                                        description="Step message"
                                    />
                                )}
                            </p>
                        ) : null}
                        <StyledButton className={styles.button} onClick={cancelParticipation}>
                            <FormattedMessage
                                defaultMessage="Cancel and select another game"
                                description="Step message"
                            />
                        </StyledButton>
                    </>
                ) : null}
            </div>
        </div>
    );
}

Wait.propTypes = propTypes;
Wait.defaultProps = defaultProps;

export default Wait;
